import type { PlateRole } from '@parsleyai/ui'
import type { Timestamp } from 'firebase/firestore'
import type { MealImagePresentation } from '@/lib/mealImages'
import type { MealImageSize, StoredMeal } from '@/types/mealPlan'
import type { BaseSliceState } from '../../types'

// FE-owned user state ABOUT a canonical meal, at
// users/{uid}/meal_user_state/{mealId} (doc id === mealId). Sparse — a doc exists
// only once the user favorites or hides the meal. Written by services/firebase/
// mealUserState. Also the favorites pool the consolidator will read.
export interface MealUserState {
  mealId: string
  favorite?: boolean
  favoritedAt?: Timestamp
  // "Removed from library" tombstone. Absent = visible. The canonical meal doc is
  // never deleted, so removal is only this flag.
  hiddenAt?: Timestamp
  // 1–5. Absent = unrated; clearing removes the key rather than storing null.
  rating?: number
}

// Per-meal cooking activity, derived from the cook log's cooked entries (not stored). See
// store/userData/mealLibrary/activity.
export interface MealCookActivity {
  cookCount: number
  lastCookedAt?: Date
}

// One meal ready for the recipes library to render — the canonical recipe joined
// with the user's state and derived cook activity. Pages consume THIS, never the
// Firestore shapes (StoredMeal / MealUserState) or the plan docs directly.
export interface LibraryMeal {
  mealId: string
  name: string
  description: string | null
  cookMins: number
  servings: number
  // Resolved rendition URLs (present only once the image pipeline is ready) +
  // presentation-shaped image lifecycle — same convention as DinnerView.
  renditions?: Partial<Record<MealImageSize, string>>
  imageState: MealImagePresentation
  createdAt: Date
  favorite: boolean
  favoritedAt?: Date
  // True when the user has removed the meal from their library. The hook returns
  // hidden meals flagged (not dropped); consumers filter.
  hidden: boolean
  // 1–5, or null when the user hasn't rated it.
  rating: number | null
  // 0 ⇒ never cooked (the card shows the New badge instead of Cooked ×N).
  cookCount: number
  lastCookedAt?: Date
  // Which plate roles the meal's components fill, in display order — drives the
  // card's role dots.
  roles: PlateRole[]
  // Search surface: component names ("Miso-Glazed Salmon") and ingredient
  // names ("salmon", "sesame oil"), pre-extracted so the fuse.js index never
  // touches the raw Firestore shapes.
  componentNames: string[]
  ingredientNames: string[]
}

// Raw slice state — the two live subscriptions' payloads, joined into LibraryMeal
// downstream by useMealLibrary. `loading` tracks the meals subscription (the
// authoritative one); user-state arriving later just pops hearts in.
export interface MealLibraryState extends BaseSliceState {
  meals: StoredMeal[]
  userState: Record<string, MealUserState>
}

export enum MealLibraryActionTypes {
  SET_MEALS = 'mealLibrary/setMeals',
  SET_USER_STATE = 'mealLibrary/setUserState',
  SET_ERROR = 'mealLibrary/error',
  CLEAR = 'mealLibrary/clear'
}

export type MealLibraryAction =
  | { type: MealLibraryActionTypes.SET_MEALS; payload: StoredMeal[] }
  | { type: MealLibraryActionTypes.SET_USER_STATE; payload: Record<string, MealUserState> }
  | { type: MealLibraryActionTypes.SET_ERROR; payload: string }
  | { type: MealLibraryActionTypes.CLEAR }
